import styled from '@emotion/styled'
import * as React from 'react'

import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { Box, Grid, GridRow } from '~ui/components/layout/Box'
import { getSpacingValue, TSingleSpace } from '~ui/styleGuide/spaces'

import { Card } from '../content/Card/Card'
import { Skeleton } from '../loadingSkeletons/Skeleton'
import { Text } from '../typography/Text/Text'
import { CartButtonIcon } from './CartButtonIcon'

type TButtonIconProps = Pick<PropsOf<typeof ButtonIcon>, 'state'>

type TProps = NoChildren &
  SkeletonProps &
  TestingProps &
  TButtonIconProps & {
    /**
     * total sum of the cart, with the currency abbreviation
     */
    cartSum?: string
    /**
     * the total number of items in the cart
     */
    cartItemCount?: number
    /**
     * cart button click handler
     */
    onCartButtonClick: () => void
    /**
     * translated label in front of the cart sum
     */
    cartSumLabel?: React.ReactNode
    /**
     * distance of the cart button icon with label from the bottom edge
     */
    bottom?: TSingleSpace
  }

// TODO sync with HeroScreen later ? screenContentResponsiveWidth = { maxWidth: `1400px`, width: `90vw` },
// also make more generic by receiving those as props
const StyledBox = styled(Box)`
  // giving the width of the centered screen content is 90vw,
  // setting right to 5vw did not work (Box was not aligned with the screen content)
  // therefore right is set to 50% and translateX value should be 45vw (50% of the content width)
  transform: translateX(45vw);
`

const cartSummaryHeightRem = 4.5
const MIN_PRICE_WIDTH = `4.5rem`

/**
 * displays bottom panel with back button and
 * cart button icon that opens mobile cart and shows number of items in the cart
 */
export const CartSummaryFixed: React.FC<TProps> = ({
  state,
  cartSum,
  cartItemCount = 0,
  cartSumLabel,
  onCartButtonClick,
  showSkeleton = false,
  bottom = `none`,
  dataTestId,
}) => {
  const offset = getSpacingValue(bottom)

  return (
    <StyledBox
      position={`fixed`}
      bottom={offset}
      // TODO zIndex
      right={`50%`}
      zIndex={2}
    >
      <Grid
        margin={`auto`}
        height={`${cartSummaryHeightRem}rem`}
        alignItems={`center`}
        justifyContent={`end`}
        justifyItems={`end`}
        gridTemplateColumns={`1fr`}
      >
        <Card
          radius={`rounded`}
          bgColor={`bgColors.card`}
          padding={{
            top: `none`,
            bottom: `none`,
            left: `none`,
            right: `medium`,
          }}
          display={`grid`}
          alignItems={`center`}
          gridAutoFlow={`column`}
          columnGap={`medium`}
          shadow={`bottomLong`}
          cursor={`pointer`}
          onClick={onCartButtonClick}
          dataTestId={dataTestId}
        >
          <CartButtonIcon
            state={state}
            cartItemCount={cartItemCount}
            emptyCartStyles={{ shadow: `medium` }}
          />
          <Skeleton showSkeleton={showSkeleton} height={`50%`}>
            <GridRow
              padding={{ vertical: `none`, horizontal: `medium` }}
              gridTemplateColumns={cartSum ? `auto minmax(${MIN_PRICE_WIDTH}, auto)` : `auto`}
              justifyContent={`start`}
              columnGap={`small`}
            >
              <Text fontSize={`m16`} contrast={cartSum ? `normal` : `low`}>
                {cartSumLabel}
              </Text>
              {cartSum && (
                <Text fontSize={`m16`} fontStyle={`bold`}>
                  {cartSum}
                </Text>
              )}
            </GridRow>
          </Skeleton>
        </Card>
      </Grid>
    </StyledBox>
  )
}
